import { createSelector } from 'reselect';
import type { AppState } from 'state/state.model';

import type { GetValidationErrorsFunc } from '../form/dlpOrderForm.selectors';
import type { DlpOrderStatus } from './order.model';

function getOrderState(state: AppState, tabId: string) {
  return state.secondaryMarket.dlpOrder.order[tabId];
}

type GetDlpOrderStatusFunc = (appState: AppState, tabId: string) => DlpOrderStatus;

export function getOrderId(state: AppState, tabId: string): string | undefined {
  return getOrderState(state, tabId)?.orderId;
}

export const makeGetDlpOrderStatus: (
  getDlpOrderValidationError: GetValidationErrorsFunc,
) => GetDlpOrderStatusFunc = getDlpOrderValidationError =>
  createSelector(
    [
      (appState: AppState, tabId: string) => getOrderState(appState, tabId),
      (appState: AppState, tabId: string) => getDlpOrderValidationError(appState, tabId),
    ],
    (orderState, errors) => {
      if (errors.filter(e => e.isMandatory).length > 0 && orderState?.status !== 'STALE') {
        return 'NOT_READY';
      }
      if (orderState?.status === undefined) {
        return 'READY';
      }
      switch (orderState.status) {
        case 'DONE':
          return 'ORDER_PLACED';
        case 'PENDING':
          return 'ORDER_PENDING';
        case 'REJECTED':
          return 'ORDER_FAILED';
        case 'STALE':
          return 'ORDER_STALE';
        default:
          return 'NOT_READY' as never;
      }
    },
  );

export function getOrderErrorMessage(state: AppState, tabId: string) {
  return getOrderState(state, tabId)?.errorMessage;
}

export function getOrderTimeout(state: AppState, tabId: string) {
  return getOrderState(state, tabId)?.timeout;
}

export function getOrderRejections(state: AppState, tabId: string) {
  return getOrderState(state, tabId)?.rejections;
}

export function getOrderExecutionTime(state: AppState, tabId: string) {
  return getOrderState(state, tabId)?.execTime;
}

export function isOrderTimeout(state: AppState, tabId: string) {
  return getOrderState(state, tabId)?.isTimeout;
}
